import type React from 'react';
import { Avatar } from '@/components';
import { formatCost, formatTokens } from '@/lib/format';
import type { AgentWithCounts, UsageResponse } from '@/lib/api';
import './UsageScreen.scss';

export interface UsageScreenProps {
  usage: UsageResponse;
  agents: AgentWithCounts[];
}

export const UsageScreen: React.FC<UsageScreenProps> = ({ usage, agents }) => {
  const maxCount = Math.max(1, ...usage.perAgent.map((a) => a.count));

  const stats = [
    { label: 'Requests this month', value: String(usage.totals.requestCount) },
    { label: 'Total tokens', value: formatTokens(usage.totals.totalTokens) },
    { label: 'Input tokens', value: formatTokens(usage.totals.inputTokens) },
    { label: 'Spend to date', value: formatCost(usage.totals.costUsd) },
  ];

  return (
    <div className="screen screen--narrow">
      <div className="screen__eyebrow">This billing period</div>
      <h1 className="screen__title">Usage</h1>

      <div className="usage__stats">
        {stats.map((stat) => (
          <div key={stat.label} className="usage__stat">
            <div className="usage__stat-value">{stat.value}</div>
            <div className="usage__stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="usage__card">
        <div className="field-label">Requests per agent</div>
        <div className="usage__bars">
          {usage.perAgent.map((row) => {
            const agent = agents.find((a) => a.id === row.agentId);
            return (
              <div key={row.agentId} className="usage__bar-row">
                <Avatar name={row.name} colour={agent?.colour ?? '#67789A'} size={26} />
                <div className="usage__bar-name">{row.name}</div>
                <div className="usage__bar-track">
                  <div
                    className="usage__bar-fill"
                    style={{ width: `${(row.count / maxCount) * 100}%` }}
                  />
                </div>
                <div className="usage__bar-count">{row.count}</div>
              </div>
            );
          })}
        </div>
        {usage.perAgent.length === 0 && (
          <div className="usage__empty">
            No conversations yet — open an agent and ask something.
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageScreen;
